import React, { useEffect, useRef, useState } from 'react';
import { Map as MapLibreMap, NavigationControl, ScaleControl } from 'maplibre-gl';
import type { LngLatBoundsLike, RequestParameters, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { pointMapLibreAtItsWorker } from './maplibreWorker';
import { attachMapToElement } from './mapHandle';

/** Where the map opens: an extent to frame, or a point and a zoom. */
export type MapCamera = { bounds: LngLatBoundsLike; padding?: number } | { center: [number, number]; zoom: number };

export type MapCanvasProps = {
    style: StyleSpecification;
    camera: MapCamera;
    /** Signs or rewrites the requests the map makes. */
    transformRequest?: (url: string) => RequestParameters | undefined;
    /** Runs once the map exists. Layer listeners and images attach here. */
    onMapReady?: (map: MapLibreMap) => void;
    /** What makes this a different map. Defaults to the content of the style, which
     *  is what a caller that builds one inline needs. A caller that updates part of
     *  the style on the map itself — see `useLiveTiles` — passes a key that leaves
     *  that part out, so its change re-points a source instead of rebuilding. */
    styleKey?: string;
    /** A bar saying what the distances on screen mean. Off by default: a map too small
     *  to read one is worse for the clutter. */
    showScale?: boolean;
    className?: string;
};

const cameraOptions = (camera: MapCamera) =>
    'bounds' in camera ? { bounds: camera.bounds, fitBoundsOptions: { padding: camera.padding ?? 48 } } : camera;

/**
 * A MapLibre map, and nothing about what it draws.
 *
 * It owns the traps that cost time to find rather than any one map's content: the
 * container size, the lifecycle, the failure message, and a camera that resolves
 * after the map is built. See ADR-003.
 */
const MapCanvas = ({ style, camera, transformRequest, onMapReady, styleKey, showScale, className }: MapCanvasProps) => {
    const container = useRef<HTMLDivElement>(null);
    const map = useRef<MapLibreMap | null>(null);
    const [mapError, setMapError] = useState<string | null>(null);

    // In refs: a new callback on each render must not rebuild the map. A rebuild
    // requests every tile again and discards the camera.
    const latestCamera = useRef(camera);
    latestCamera.current = camera;
    const root = useRef<HTMLDivElement | null>(null);
    const mapReady = useRef<MapCanvasProps['onMapReady']>(undefined);
    mapReady.current = onMapReady;
    const signRequest = useRef<MapCanvasProps['transformRequest']>(undefined);
    signRequest.current = transformRequest;

    // Callers build a style inline, so a check on the reference rebuilds the map on
    // every render.
    const mapKey = styleKey ?? JSON.stringify(style);

    useEffect(() => {
        if (!container.current) return undefined;

        pointMapLibreAtItsWorker();

        const instance = new MapLibreMap({
            container: container.current,
            style,
            ...cameraOptions(latestCamera.current),
            // MapLibre cross-fades symbols between tile zooms but not circles, so a
            // default fade leaves a symbol behind the circle it labels.
            fadeDuration: 0,
            transformRequest: url => signRequest.current?.(url),
        });

        // A failed style or a rejected tile gives an empty canvas and no message.
        instance.on('error', event => setMapError(event?.error?.message ?? 'The map could not be loaded.'));

        instance.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
        // Same corner as the zoom buttons, added after them so it stacks underneath.
        if (showScale) instance.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-right');
        map.current = instance;
        attachMapToElement(root.current, instance);
        mapReady.current?.(instance);

        return () => {
            map.current = null;
            instance.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapKey]);

    // A camera that resolves after the map is built must still be obeyed. A rebuild
    // would request every tile again and discard the current view, so move instead.
    const boundsKey = 'bounds' in camera ? JSON.stringify(camera.bounds) : null;
    useEffect(() => {
        if (!map.current || !boundsKey) return;
        map.current.fitBounds(JSON.parse(boundsKey), { padding: 'bounds' in camera ? camera.padding ?? 48 : 48 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundsKey]);

    return (
        <div ref={root} className={`relative ${className ?? 'h-full w-full'}`} data-testid='map-canvas'>
            {/* Sized, not positioned: maplibre-gl.css puts `position: relative` on the
                container it takes over, which beats `absolute inset-0` and leaves the
                box with no height at all — no basemap, and no tile requests. */}
            <div ref={container} className='h-full w-full' />

            {mapError && (
                <div
                    role='alert'
                    className='absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-lg border border-destructive bg-white px-3 py-2 text-sm text-destructive shadow-sm'
                >
                    {mapError}
                </div>
            )}
        </div>
    );
};

export default MapCanvas;
